import {
    Button,
    Card,
    Chip,
    Inline,
    Page,
    Skeleton,
    Stack,
    Text,
    toast
} from '@7shifts/sous-chef';
import { __ } from 'i18n/translate';
import { Company, CompanyStats } from 'adminPanel/types';
import { useNavigate } from 'react-router-dom';
import { useCompanyUsers } from 'adminPanel/modules/Companies/hooks/useCompanyUsers';
import { useCompanyQueues } from 'adminPanel/modules/Companies/hooks/useCompanyQueues';
import { useCompanyChannels } from '../hooks/useCompanyChannels';
import { format, parseISO } from 'date-fns/esm';
import { useEffect, useState } from 'react';
import {
    disconnectAllCompanyUsers,
    getCompanyStats,
    updateCompany
} from 'adminPanel/api/companies';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

type Props = {
    company: Company;
    onUpdate?: (updated: Company) => void;
};

const UsageBar = ({
    label,
    used,
    limit,
    isLoading
}: {
    label: string;
    used: number;
    limit: number;
    isLoading: boolean;
}) => {
    const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
    const color = pct >= 90 ? '#e03131' : pct >= 70 ? '#f59f00' : '#2f9e44';
    return (
        <div style={{ flex: 1 }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                    fontSize: 13
                }}
            >
                <span style={{ fontWeight: 600 }}>{label}</span>
                {isLoading ? (
                    <Skeleton width={50} height={14} />
                ) : (
                    <span style={{ color }}>
                        {used} / {limit}
                    </span>
                )}
            </div>
            <div
                style={{
                    height: 8,
                    background: '#e9ecef',
                    borderRadius: 4,
                    overflow: 'hidden'
                }}
            >
                {!isLoading && (
                    <div
                        style={{
                            height: '100%',
                            width: `${pct}%`,
                            background: color,
                            borderRadius: 4,
                            transition: 'width 0.4s ease'
                        }}
                    />
                )}
            </div>
        </div>
    );
};

const CompanyOverviewPage = ({ company, onUpdate }: Props) => {
    const navigate = useNavigate();
    const { users, isLoading: isLoadingUsers } = useCompanyUsers(company.id);
    const { queues, isLoading: isLoadingQueues } = useCompanyQueues(company.id);
    const { channels, isLoading: isLoadingChannels } = useCompanyChannels(
        company.id
    );
    const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);

    const [editingDueDate, setEditingDueDate] = useState(false);
    const [dueDateValue, setDueDateValue] = useState(
        company.dueDate ? company.dueDate.substring(0, 10) : ''
    );
    const [savingDueDate, setSavingDueDate] = useState(false);
    const [togglingStatus, setTogglingStatus] = useState(false);
    const [disconnectingAll, setDisconnectingAll] = useState(false);

    useEffect(() => {
        getCompanyStats(company.id)
            .then(setCompanyStats)
            .finally(() => setIsLoadingStats(false));
    }, [company.id]);

    const handleSaveDueDate = async () => {
        setSavingDueDate(true);
        try {
            const updated = await updateCompany(company.id, {
                dueDate: dueDateValue
            });
            toast('Data de vencimento atualizada!');
            setEditingDueDate(false);
            onUpdate?.(updated);
        } catch {
            toast('Erro ao atualizar vencimento', 'danger');
        } finally {
            setSavingDueDate(false);
        }
    };

    const handleToggleStatus = async () => {
        setTogglingStatus(true);
        try {
            const updated = await updateCompany(company.id, {
                status: !company.status
            });
            toast(
                company.status
                    ? 'Empresa inativada com sucesso'
                    : 'Empresa ativada com sucesso'
            );
            onUpdate?.(updated);
        } catch {
            toast('Erro ao alterar status da empresa', 'danger');
        } finally {
            setTogglingStatus(false);
        }
    };

    const handleDisconnectAll = async () => {
        const confirmed = window.confirm(
            `Desconectar TODAS as sessões de WhatsApp da empresa "${company.name}"? Os atendimentos ficarão offline até reconectar.`
        );
        if (!confirmed) {
            return;
        }
        setDisconnectingAll(true);
        try {
            await disconnectAllCompanyUsers(company.id);
            toast('Todas as sessões de WhatsApp foram desconectadas');
        } catch {
            toast('Erro ao desconectar as sessões', 'danger');
        } finally {
            setDisconnectingAll(false);
        }
    };

    const planLimit = company.plan;

    // Tickets donut chart
    const totalTickets = companyStats
        ? companyStats.openTickets +
          companyStats.pendingTickets +
          companyStats.closedTickets
        : 0;
    const ticketsChart: { series: number[]; options: ApexOptions } = {
        series: companyStats
            ? [
                  companyStats.openTickets,
                  companyStats.pendingTickets,
                  companyStats.closedTickets
              ]
            : [],
        options: {
            chart: { type: 'donut', toolbar: { show: false } },
            labels: ['Em aberto', 'Pendentes', 'Fechados'],
            colors: ['#339af0', '#f59f00', '#868e96'],
            legend: { position: 'bottom', fontSize: '12px' },
            dataLabels: { enabled: false },
            plotOptions: {
                pie: {
                    donut: {
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: 'Total',
                                color: '#495057',
                                formatter: () => String(totalTickets)
                            }
                        }
                    }
                }
            }
        }
    };

    return (
        <Page
            title={__('adminpanel.overview')}
            breadcrumbs={{ label: __('adminpanel.companies'), url: '../../' }}
        >
            <Stack space={20}>
                {/* Header card */}
                <div
                    style={{
                        background: '#fff',
                        border: '1px solid #e9ecef',
                        borderRadius: 10,
                        padding: 20
                    }}
                >
                    {/* Name + status + discrete toggle */}
                    <Inline space={8} alignItems="center">
                        <Text as="h2">{company.name}</Text>
                        <Text as="caption" color="grey-300">
                            #{company.id}
                        </Text>
                        {company.status ? (
                            <Chip theme="success">Ativa</Chip>
                        ) : (
                            <Chip theme="danger">
                                {__('adminpanel.inactive')}
                            </Chip>
                        )}
                        <button
                            onClick={handleToggleStatus}
                            disabled={togglingStatus}
                            title={
                                company.status
                                    ? 'Inativar empresa'
                                    : 'Ativar empresa'
                            }
                            style={{
                                background: 'none',
                                border: '1px solid #dee2e6',
                                borderRadius: 4,
                                padding: '2px 8px',
                                fontSize: 11,
                                color: company.status ? '#e03131' : '#2f9e44',
                                cursor: togglingStatus
                                    ? 'not-allowed'
                                    : 'pointer',
                                opacity: togglingStatus ? 0.6 : 1
                            }}
                        >
                            {company.status ? 'Inativar' : 'Ativar'}
                        </button>
                        <button
                            onClick={handleDisconnectAll}
                            disabled={disconnectingAll}
                            title="Desconectar todas as sessões de WhatsApp da empresa"
                            style={{
                                background: 'none',
                                border: '1px solid #dee2e6',
                                borderRadius: 4,
                                padding: '2px 8px',
                                fontSize: 11,
                                color: '#e03131',
                                cursor: disconnectingAll
                                    ? 'not-allowed'
                                    : 'pointer',
                                opacity: disconnectingAll ? 0.6 : 1
                            }}
                        >
                            {disconnectingAll
                                ? 'Desconectando...'
                                : 'Desconectar todos'}
                        </button>
                    </Inline>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: 16,
                            marginTop: 16
                        }}
                    >
                        {/* Plan */}
                        <div>
                            <p
                                style={{
                                    fontSize: 11,
                                    color: '#868e96',
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    margin: '0 0 4px'
                                }}
                            >
                                Plano
                            </p>
                            <p style={{ margin: 0, fontSize: 14 }}>
                                {planLimit?.name ?? '—'}
                            </p>
                        </div>

                        {/* Due date */}
                        <div>
                            <p
                                style={{
                                    fontSize: 11,
                                    color: '#868e96',
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    margin: '0 0 4px'
                                }}
                            >
                                Vencimento
                            </p>
                            {editingDueDate ? (
                                <Inline space={8} alignItems="center">
                                    <input
                                        type="date"
                                        value={dueDateValue}
                                        onChange={(e) =>
                                            setDueDateValue(e.target.value)
                                        }
                                        style={{
                                            fontSize: 13,
                                            padding: '4px 8px',
                                            border: '1px solid #ced4da',
                                            borderRadius: 4
                                        }}
                                    />
                                    <Button
                                        size="min-width-100"
                                        theme="primary"
                                        onClick={handleSaveDueDate}
                                        disabled={savingDueDate}
                                    >
                                        Salvar
                                    </Button>
                                    <Button
                                        size="min-width-100"
                                        theme="hollow"
                                        onClick={() => setEditingDueDate(false)}
                                    >
                                        Cancelar
                                    </Button>
                                </Inline>
                            ) : (
                                <Inline space={8} alignItems="center">
                                    <span style={{ fontSize: 14 }}>
                                        {company.dueDate
                                            ? format(
                                                  parseISO(company.dueDate),
                                                  'dd/MM/yyyy'
                                              )
                                            : '—'}
                                    </span>
                                    <Button
                                        size="min-width-100"
                                        theme="hollow"
                                        onClick={() => setEditingDueDate(true)}
                                    >
                                        Editar
                                    </Button>
                                </Inline>
                            )}
                        </div>

                        {/* Recurrence */}
                        {company.recurrence && (
                            <div>
                                <p
                                    style={{
                                        fontSize: 11,
                                        color: '#868e96',
                                        textTransform: 'uppercase',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        margin: '0 0 4px'
                                    }}
                                >
                                    Recorrência
                                </p>
                                <p style={{ margin: 0, fontSize: 14 }}>
                                    {company.recurrence}
                                </p>
                            </div>
                        )}

                        {/* Messages */}
                        <div>
                            <p
                                style={{
                                    fontSize: 11,
                                    color: '#868e96',
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    letterSpacing: '0.5px',
                                    margin: '0 0 4px'
                                }}
                            >
                                Mensagens
                            </p>
                            <p style={{ margin: 0, fontSize: 14 }}>
                                {isLoadingStats ? (
                                    <Skeleton width={60} height={16} />
                                ) : (
                                    (companyStats?.totalMessages.toLocaleString(
                                        'pt-BR'
                                    ) ?? '—')
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Plan consumption */}
                {planLimit && (
                    <div
                        style={{
                            background: '#fff',
                            border: '1px solid #e9ecef',
                            borderRadius: 10,
                            padding: 20
                        }}
                    >
                        <p style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 15 }}>
                            Consumo do plano
                        </p>
                        <Stack space={16}>
                            <UsageBar
                                label="Usuários"
                                used={users.length}
                                limit={planLimit.users}
                                isLoading={isLoadingUsers}
                            />
                            <UsageBar
                                label="Canais"
                                used={channels.length}
                                limit={planLimit.connections}
                                isLoading={isLoadingChannels}
                            />
                            <UsageBar
                                label="Filas"
                                used={queues.length}
                                limit={planLimit.queues}
                                isLoading={isLoadingQueues}
                            />
                        </Stack>
                    </div>
                )}

                {/* Charts + quick nav */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 16
                    }}
                >
                    {/* Tickets donut */}
                    <Card>
                        <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 14 }}>
                            Tickets
                        </p>
                        {isLoadingStats ? (
                            <Skeleton width="100%" height={200} />
                        ) : totalTickets === 0 ? (
                            <p
                                style={{
                                    color: '#adb5bd',
                                    fontSize: 13,
                                    marginTop: 16
                                }}
                            >
                                Nenhum ticket encontrado.
                            </p>
                        ) : (
                            <ReactApexChart
                                options={ticketsChart.options}
                                series={ticketsChart.series}
                                type="donut"
                                height={200}
                            />
                        )}
                    </Card>

                    {/* Quick nav */}
                    <Stack space={8}>
                        <Card onClick={() => navigate('../users')}>
                            <Inline
                                space={8}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Text>Usuários</Text>
                                {isLoadingUsers ? (
                                    <Skeleton width={30} height={24} />
                                ) : (
                                    <strong style={{ fontSize: 20 }}>
                                        {users.length}
                                    </strong>
                                )}
                            </Inline>
                        </Card>
                        <Card onClick={() => navigate('../queues')}>
                            <Inline
                                space={8}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Text>Filas</Text>
                                {isLoadingQueues ? (
                                    <Skeleton width={30} height={24} />
                                ) : (
                                    <strong style={{ fontSize: 20 }}>
                                        {queues.length}
                                    </strong>
                                )}
                            </Inline>
                        </Card>
                        <Card onClick={() => navigate('../channels')}>
                            <Inline
                                space={8}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Text>Canais</Text>
                                {isLoadingChannels ? (
                                    <Skeleton width={30} height={24} />
                                ) : (
                                    <strong style={{ fontSize: 20 }}>
                                        {channels.length}
                                    </strong>
                                )}
                            </Inline>
                        </Card>
                    </Stack>
                </div>
            </Stack>
        </Page>
    );
};

export default CompanyOverviewPage;
